import {useNumericFormatSettings} from '_contexts/MySettingsProvider';
import dayjs from 'dayjs';

import {useParseWithSettings} from './LFCParseUtil';

/**
 * 年月表示
 * @param value
 * @returns
 */
export const fmtYYYYMM = (value: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('YYYY-MM');
};

/**
 * 年月日表示
 * @param value
 * @returns
 */
export const fmtYYYYMMDD = (value: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('YYYY-MM-DD');
};

/**
 * 年月日時表示
 * @param value
 * @returns
 */
export const fmtYYYYMMDDHH = (value: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('YYYY-MM-DD HH');
};

/**
 * 年月日時分秒表示
 * @param value
 * @returns
 */
export const fmtYYYYMMDDHHmmss = (value: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * 年月日時分表示
 * @param value
 * @returns
 */
export const fmtYYYYMMDDHHmm = (value: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('YYYY-MM-DD HH:mm');
};

/**
 * 日付省略表示
 * @param value
 * @returns
 */
export const fmtYMD = (value?: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('YY-MM-DD HH:mm:ss');
};

/**
 * 日付省略表示
 * @param value
 * @returns
 */
export const fmtHHMM = (value?: any) => {
  if (!value) {
    return;
  }
  return dayjs(String(value)).format('HH:mm');
};

/**
 * 時間省略表示
 * @param epoch
 * @returns
 */
export const fmtmmss = (epoch: any) => {
  if (!epoch) {
    return;
  }
  return dayjs.unix(Number(epoch)).format('mm:ss');
};

/**
 * 四捨五入、下１桁 表示用
 * @param prms
 * @returns
 */
export const fmtRoundOffZero = (value: any): string => {
  if (!value) {
    return '0.0';
  }
  return (Math.round(value * 10) / 10).toFixed(1);
};

/**
 * 3桁カンマ区切り数値フォーマット
 * @param value フォーマットする値
 * @returns カンマ区切りされた数値文字列
 */
export const fmtThreeCommas = (value: any): string => {
  if (!value || value === 'null') {
    return '0';
  }
  return Number(value).toLocaleString();
};

/**
 * MySettings設定を適用したフォーマット関数を取得するフック
 */
export const useFormatWithSettings = () => {
  const settings = useNumericFormatSettings();
  const parsers = useParseWithSettings();

  return {
    /**
     * 整数項目 表示用
     * MySettings設定を適用した整数フォーマット（カンマ区切り、小数点以下なし）
     * @param value フォーマットする値
     * @returns フォーマットされた整数文字列
     */
    fmtInteger: (value: number | null): string => {
      if (value == null || !isFinite(value)) {
        return '-';
      }
      return parsers.parseNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },

    /**
     * 小数項目 表示用
     * MySettings設定を適用した数値フォーマット
     * @param value フォーマットする値
     * @returns フォーマットされた数値文字列
     */
    fmtFloat: (value: number | null): string => {
      if (value == null || !isFinite(value)) {
        return '-';
      }
      return parsers.parseNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: settings.defaultPrecision,
        maximumFractionDigits: settings.defaultPrecision
      });
    },

    /**
     * パーセント項目 表示用
     * MySettings設定を適用したパーセントフォーマット
     * @param value フォーマットする値
     * @returns フォーマットされたパーセント文字列
     */
    fmtPercent: (value: number | null): string => {
      if (value == null || !isFinite(value)) {
        return '-';
      }
      return `${parsers.parsePercent(value).toLocaleString(undefined, {
        minimumFractionDigits: settings.percentPrecision,
        maximumFractionDigits: settings.percentPrecision
      })} %`;
    },

    /**
     * サイズ項目 表示用（mm単位）
     * MySettings設定を適用したサイズフォーマット
     * @param value フォーマットする値
     * @returns フォーマットされたサイズ文字列（mm単位）
     */
    fmtSizeByMm: (value: number | null): string => {
      if (value == null || !isFinite(value)) {
        return '-';
      }
      return `${parsers.parseSize(value).toLocaleString(undefined, {
        minimumFractionDigits: settings.sizePrecision,
        maximumFractionDigits: settings.sizePrecision
      })} mm`;
    }
  };
};
